import { useState, useCallback } from "react";
import { Button, MenuItem, Menu } from "@mui/material";
import toast from "react-hot-toast";
import { supabase } from "../../../../../supabase";
import hasMinimalData from "./utils/hasMinimalData";
import normalizeText from "./utils/normalizeText";
import fetchInfluencerData from "../../../../../api/fetchInfluencerData";
import { validatePaidCheckData } from "../../../../../utils/validation";
import limitArraySize from "../../../../../utils/limitArraySize";
import preserveExistingData from "../../../../../utils/preserveExistingData";
import insertHistoryIfNotDuplicate from "../../../../../utils/insertHistoryIfNotDuplicate";
import { useMainContext } from "../../../../../context/useMainContext";
import ManualCheck from "./ManualCheck";
import BulkCheckModal from "./BulkCheckModal";

export default () => {
  const {
    selected,
    isGuest,
    fetchDataTable,
    setRows,
    user,
    setLoadingData,
  } = useMainContext();
  const [isChecksOpen, setIsChecksOpen] = useState(null);
  const [manualCheckModalOpen, setManualCheckModalOpen] = useState(false);
  const [bulkCheckModalOpen, setBulkCheckModalOpen] = useState(false);
  const [currentCheckType, setCurrentCheckType] = useState(null);
  const openChecks = Boolean(isChecksOpen);
  const handleCloseChecks = () => setIsChecksOpen(null);

  const handleRunFreeCheck = useCallback(async (selectedItems) => {
    setLoadingData(true);
    try {
      for (const influencer of selectedItems) {
        if (!influencer) continue;

        // Use the influencer ID directly from selected object
        const influencerId = influencer.id;

        if (!influencerId) {
          toast.error(
            `Invalid influencer ID for ${influencer.username || influencer.link}`
          );
          continue;
        }

        try {
          const apiData = await fetchInfluencerData({
            platform: influencer.platform,
            username: influencer.username,
            type: 'free',
          });

          if (!apiData) {
            toast.error(`Failed to fetch data for ${influencer.username}`);
            continue;
          }

          // Check if response contains no useful data
          if (hasMinimalData(apiData, "free")) {
            toast(
              `No data available for ${influencer.username} - profile may be private or have insufficient data`,
              { icon: "ℹ️" }
            );
            continue;
          }

          // Prepare new data from API
          const newFreeCheckData = {
            influencer_id: influencerId,
            quality_score: apiData.typeScore || apiData.user_typeScore,
            reach: Array.isArray(apiData.postArray)
              ? apiData.postArray.reduce(
                  (acc, post) => acc + (post.reach || 0),
                  0
                )
              : 0,
            posts: apiData.posts,
            followers: apiData.followers,
            language: apiData.language,
            country: apiData.country,
            city: apiData.city,
            website: apiData.website,
            gender: apiData.gender,
            bio: apiData.description,
            profile_pic_url: apiData.profilePicURL || apiData.profilePicUrl,
            is_private: apiData.isPrivate,
            median_views_per_post: apiData.medianViewsPerPost,
            median_plays_per_reel: apiData.medianPlaysPerReel,
            following: apiData.following,
            public_email: apiData.publicEmail,
            firstname: normalizeText(apiData.displayName),
            display_name: apiData.displayName,
            avg_likes_per_post: apiData.avgLikesPerPost,
            avg_comments_per_post: apiData.avgCommentsPerPost,
            contact_email: apiData.collaborationInfo?.contactMail,
            contact_whatsapp: apiData.collaborationInfo?.whatsApp,
            contact_link: apiData.collaborationInfo?.contactLink,
            management_company: apiData.collaborationInfo?.management,
            youtube_channel: apiData.socialHandles?.youtube,
            youtube_username: apiData.socialHandles?.youtubeUsername,
            monthly_growth_difference:
              apiData.monthlyGrowthObject?.monthlyDifference,
            monthly_growth_change: apiData.monthlyGrowthObject?.monthlyChange,
            instagram_id: apiData.instagramId,
            insta_category: apiData.instaCategory,
            is_free_checked: true,
          };

          // Get existing free check data to preserve important fields
          const existingFreeCheck = influencer.influencer_free_checks || {};

          // Apply data preservation logic before saving
          const updatesToSave = preserveExistingData(
            newFreeCheckData,
            existingFreeCheck
          );

          const { error } = await supabase
            .from("influencer_free_checks")
            .upsert(updatesToSave, { onConflict: "influencer_id" });

          if (error) throw error;

          // Handle history insertion with duplicate checking
          await insertHistoryIfNotDuplicate({
            tableName: "free_check_history",
            influencerId,
            apiData,
            username: influencer.username,
            user,
          });

        } catch (err) {
          console.error(
            `Error during free check for ${influencer.username}:`,
            err
          );
          toast.error(`Free check failed: ${influencer.link}`);
        }
      }

      toast.success("Free Check completed");
    } catch (error) {
      console.error("Error during free check:", error);
      toast.error("Free Check failed");
    } finally {
      setLoadingData(false);
    }
  }, [setLoadingData, setRows, user]);

  const handleRunPaidCheck = useCallback(async (selectedItems) => {
    setLoadingData(true);
    try {
      for (const influencer of selectedItems) {
        if (!influencer) continue;
        
        // Use the influencer ID directly from selected object
        const influencerId = influencer.id;
        
        if (!influencerId) {
          toast.error(`Invalid influencer ID for ${influencer.username || influencer.link}`);
          continue;
        }

        try {
          const apiData = await fetchInfluencerData({
            platform: influencer.platform,
            username: influencer.username,
            type: 'paid',
          });

          if (!apiData) {
            toast.error(`Failed to fetch data for ${influencer.username}`);
            continue;
          }

          // Check if response contains no useful data
          if (hasMinimalData(apiData, "paid")) {
            toast(
              `No data available for ${influencer.username} - profile may be private or have insufficient data`,
              { icon: "ℹ️" }
            );
            continue;
          }

          // Prepare new paid check data from API
          const newPaidCheckData = {
            influencer_id: influencerId,
            quality_score: apiData?.typeScore || apiData?.user_typeScore,
            // Store the DACH audience percentage in the specific column
            audience_dach: (() => {
              const countryArray = apiData?.audienceAnalysis?.countryArray || [];
              const dachCountries = ["austria", "germany", "switzerland"];
              const dachSum = countryArray
                .filter(
                  (item) =>
                    item.country &&
                    dachCountries.includes(item.country.toLowerCase())
                )
                .reduce(
                  (sum, item) =>
                    sum +
                    (typeof item.value === "number"
                      ? item.value
                      : Number(item.value) || 0),
                  0
                );
              return dachSum > 0 ? dachSum : null;
            })(),
            // Get German language percentage from languageArray
            audience_language: (() => {
              const languageArray =
                apiData?.audienceAnalysis?.languageArray || [];

              const germanData = languageArray.find(
                (item) => item.language?.toLowerCase() === "de"
              );
              return germanData?.value ? Number(germanData.value) : null;
            })(),
            // Store the German language percentage in the specific column
            language_german: (() => {
              const languageArray =
                apiData?.audienceAnalysis?.languageArray || [];
              const germanData = languageArray.find(
                (item) => item.language?.toLowerCase() === "de"
              );
              return germanData?.value ? Number(germanData.value) : null;
            })(),
            audience_city: apiData?.audienceAnalysis?.cityArray?.[0]?.city,
            // Store full arrays for reference
            audience_countries_full: JSON.stringify(
              limitArraySize(apiData?.audienceAnalysis?.countryArray || [], 50)
            ),
            audience_languages_full: JSON.stringify(
              limitArraySize(apiData?.audienceAnalysis?.languageArray || [], 50)
            ),
            audience_cities_full: JSON.stringify(
              limitArraySize(apiData?.audienceAnalysis?.cityArray || [], 50)
            ),

            audience_gender_male: apiData?.audienceAnalysis?.genderMtoF?.male,
            audience_gender_female: apiData?.audienceAnalysis?.genderMtoF?.female,

            audience_real_percentage:
              apiData?.audienceAnalysis?.audienceType?.realPeoplePercentage,
            audience_influencers_percentage:
              apiData?.audienceAnalysis?.audienceType?.influencersPercentage,
            audience_mass_followers_percentage:
              apiData?.audienceAnalysis?.audienceType?.massFollowersPercentage,
            audience_suspicious_percentage:
              apiData?.audienceAnalysis?.audienceType?.suspiciousPercentage,

            audience_median_age: apiData?.audienceAnalysis?.birthyearMedian,
            audience_age_13_17: apiData?.audienceAnalysis?.ageRange?.["13-17"],
            audience_age_18_24: apiData?.audienceAnalysis?.ageRange?.["18-24"],
            audience_age_25_34: apiData?.audienceAnalysis?.ageRange?.["25-34"],
            audience_age_35_44: apiData?.audienceAnalysis?.ageRange?.["35-44"],
            audience_age_45_plus: apiData?.audienceAnalysis?.ageRange?.["45+"],
            avg_likes_per_post: apiData?.avgLikesPerPost,
            avg_comments_per_post: apiData?.avgCommentsPerPost,
            monthly_growth_followers: apiData?.monthlyGrowthFollowers,
            monthly_growth_percentage: apiData?.monthlyGrowthPercentage,
            posts_per_week: apiData?.postsPerWeek,
            reels_per_week: apiData?.reelsPerWeek,
            sponsored_posts_percentage: apiData?.sponsoredPostsPercentage,
            sponsored_reels_percentage: apiData?.sponsoredReelsPercentage,
            is_paid_checked: true,
            authenticity_score: apiData?.profileScore?.suspiciousFollowersObject?.score || null,
            story_views_min: apiData.medianViewsPerStoryObject?.from,
            story_views_max: apiData.medianViewsPerStoryObject?.to,
            story_views_avg: (apiData.medianViewsPerStoryObject?.from + apiData.medianViewsPerStoryObject?.to) / 2,
            story_frequency: apiData?.profileScore?.storyFrequencyObject?.actualValue,
            average_age: apiData?.audienceAnalysis?.birthyearMedian,
          };

          // Get existing paid check data to preserve important fields
          const existingPaidCheck = influencer.influencer_paid_checks || {};

          // Apply data preservation logic and validate before saving
          const preservedData = preserveExistingData(
            newPaidCheckData,
            existingPaidCheck
          );
          const updatesToSave = validatePaidCheckData(preservedData);

          const { error } = await supabase
            .from("influencer_paid_checks")
            .upsert(updatesToSave, { onConflict: "influencer_id" });
          if (error) throw error;

          // Handle history insertion with duplicate checking
          await insertHistoryIfNotDuplicate({
            tableName: "paid_check_history",
            influencerId,
            apiData,
            username: influencer.username,
            user,
          });

        } catch (err) {
          console.error(
            `Error during paid check for ${influencer.username}:`,
            err
          );
          toast.error(`Paid check failed: ${influencer.link}`);
        }
      }

      toast.success("Paid Check completed");
    } catch (error) {
      console.error("Error during paid check:", error);
      toast.error("Paid Check failed");
    } finally {
      setLoadingData(false);
    }
    
  }, [setRows, user, setLoadingData]);

  const handleCheck = useCallback(async (checkType) => {
    if (isGuest) {
      toast.error(`Guests cannot perform ${checkType} Check.`);
      return;
    }
    if (selected.length === 0) {
      toast.error("Please select at least one profile.");
      return;
    }
    if (selected.length === 1 && checkType === "free") {
      await handleRunFreeCheck(selected);
      await fetchDataTable();
      return;
    }
    setCurrentCheckType(checkType);
    setBulkCheckModalOpen(true);
  }, [isGuest, selected, handleRunFreeCheck, fetchDataTable]);

  const handleManualCheck = useCallback(() => {
    if (isGuest) {
      toast.error("Guests cannot perform Manual Check.");
      return;
    }
    if (selected.length === 0) {
      toast.error("Please select at least one profile.");
      return;
    }
    if (selected.length > 1) {
      toast.error("Please select only one profile for Manual Check.");
      return;
    }

    setManualCheckModalOpen(true);
  }, [isGuest, selected, setManualCheckModalOpen]);

  const handleBulkCheckConfirm = useCallback(async () => {
    setBulkCheckModalOpen(false);
    if (currentCheckType === "free") {
      await handleRunFreeCheck(selected);
    } else if (currentCheckType === "paid") {
      await handleRunPaidCheck(selected);
    }
    await fetchDataTable();
  }, [currentCheckType, selected, handleRunFreeCheck, handleRunPaidCheck, fetchDataTable]);

  if (isGuest) return null;

  return (
    <>
      <Button
        variant="outlined"
        onClick={(e) => setIsChecksOpen(e.currentTarget)}
        aria-haspopup="true"
        aria-expanded={openChecks ? "true" : undefined}
      >
        Checks
      </Button>

      <Menu
        anchorEl={isChecksOpen}
        open={openChecks}
        onClose={handleCloseChecks}
      >
        <MenuItem onClick={() => [handleCloseChecks(), handleCheck("free")]}>
          Free
        </MenuItem>
        <MenuItem onClick={() => [handleCloseChecks(), handleManualCheck()]}>
          Manual
        </MenuItem>
        <MenuItem onClick={() => [handleCloseChecks(), handleCheck("paid")]}>
          Paid
        </MenuItem>
      </Menu>

      <ManualCheck
        {...{
          manualCheckModalOpen,
          setManualCheckModalOpen,
        }}
      />

      <BulkCheckModal
        {...{
          bulkCheckModalOpen,
          setBulkCheckModalOpen,
          handleBulkCheckConfirm,
          currentCheckType,
        }}
      />
    </>
  );
};
